// ---------------------------------------------------------------------------
// Phase 6 of STATE_MANAGEMENT_MIGRATION_PLAN.md
//
// Replaces the dual React Context pattern (WorkspaceContext + WorkspaceDispatchContext)
// with a Zustand store. The store holds workspace shell state: which workspace
// is active, its data, loading/error states, permissions, and activity bar sizing.
//
// Side-effect policy:
//   - Store actions are synchronous domain transitions only.
//   - Remote IO stays in Apollo/React Query hooks (useWorkspace).
//   - The store provides inputs; it does not fetch.
//   - Narrow local persistence (sessionStorage for workspaceId) is in the controller.
// ---------------------------------------------------------------------------
import type { ApolloError } from "@apollo/client";
import type { Draft } from "immer";
import { Subject } from "rxjs";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import type { Workspace } from "models/workspace";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Permissions derived from workspace type + user role. */
export interface WorkspacePermissions {
  readonly canView: boolean;
  readonly canEdit: boolean;
  readonly canDelete: boolean;
}

/** Plain serialisable workspace state — no functions, no Subjects. */
interface WorkspaceState {
  readonly workspaceId: Maybe<string>;
  readonly workspace: Maybe<Workspace>;
  readonly activeDashboardId: Maybe<string>;
  readonly activityBarWidth: number;
  readonly isActivityBarResizing: boolean;
  readonly isLoading: boolean;
  readonly error: Maybe<ApolloError>;
  readonly permissions: Maybe<WorkspacePermissions>;
}

/** Synchronous domain transitions. */
interface WorkspaceActions {
  setWorkspaceId: (id: Maybe<string>) => void;
  setWorkspace: (workspace: Maybe<Workspace>) => void;
  setActiveDashboardId: (id: Maybe<string>) => void;
  setActivityBarWidth: (width: number) => void;
  setActivityBarResizing: (resizing: boolean) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Maybe<ApolloError>) => void;
  setPermissions: (permissions: Maybe<WorkspacePermissions>) => void;
  requestRefresh: () => void;
  reset: () => void;
}

export type WorkspaceStore = WorkspaceState & WorkspaceActions;

// ---------------------------------------------------------------------------
// RxJS Subject — preserved for backward compat with WorkspaceWrapper / DashboardWrapper
// subscription pattern. Once those are fully migrated to subscribeWithSelector
// callbacks, this Subject can be removed.
// ---------------------------------------------------------------------------
export const workspaceRefreshSubject = new Subject<string>();

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------
const initialState: WorkspaceState = {
  workspaceId: undefined,
  workspace: undefined,
  activeDashboardId: undefined,
  activityBarWidth: 420,
  isActivityBarResizing: false,
  isLoading: false,
  error: undefined,
  permissions: undefined
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------
export const useWorkspaceStore = create<WorkspaceStore>()(
  subscribeWithSelector(
    immer((set) => ({
      ...initialState,

      setWorkspaceId: (id) =>
        set((s) => {
          s.workspaceId = id;
        }),

      setWorkspace: (workspace) =>
        set((s) => {
          s.workspace = workspace;
        }),

      setActiveDashboardId: (id) =>
        set((s) => {
          s.activeDashboardId = id;
        }),

      setActivityBarWidth: (width) =>
        set((s) => {
          s.activityBarWidth = width;
        }),

      setActivityBarResizing: (resizing) =>
        set((s) => {
          s.isActivityBarResizing = resizing;
        }),

      setLoading: (loading) =>
        set((s) => {
          s.isLoading = loading;
        }),

      setError: (error) =>
        set((s) => {
          // Immer's WritableDraft strips readonly from ApolloError.graphQLErrors.
          // Draft cast is safe here — Immer manages mutability internally.
          s.error = error as Maybe<Draft<ApolloError>>;
        }),

      setPermissions: (permissions) =>
        set((s) => {
          s.permissions = permissions;
        }),

      requestRefresh: () =>
        workspaceRefreshSubject.next(
          `${Date.now()}-${Math.random().toString(36).slice(2)}`
        ),

      reset: () => set(initialState)
    }))
  )
);

// ---------------------------------------------------------------------------
// Named selectors — keep component subscriptions narrow
// ---------------------------------------------------------------------------
export const selectWorkspaceId = (s: WorkspaceStore): Maybe<string> => s.workspaceId;

export const selectWorkspace = (s: WorkspaceStore): Maybe<Workspace> => s.workspace;

export const selectIsLoading = (s: WorkspaceStore): boolean => s.isLoading;
